/**
 * Cleaned-up version of StateGameStatsComponent that ensures the total scanned board count from all states matches the filteredStats unique count.
 */

import { Component, OnInit, ChangeDetectorRef, Input } from '@angular/core';
import { BoardStats } from 'src/app/core/models/boardStats.model';
import { BoardStatService, GroupedStats } from 'src/app/core/services/boardstats.service';
import { LoginService } from 'src/app/core/services/login.service';
import { LocationService } from 'src/app/core/services/location.service';
import { UserType } from 'src/app/core/models/userType.models';
import { BoardService } from 'src/app/core/services/board.service';

@Component({
  selector: 'app-state-game-stats',
  templateUrl: './state-game-stats.component.html',
  styleUrls: ['./state-game-stats.component.scss']
})
export class StateGameStatsComponent implements OnInit {
  @Input() boardStats: BoardStats[] = [];
  @Input() startDate: Date;
  @Input() endDate: Date;

  groupedStats: { state: string, stats: GroupedStats, uniqueBoardCount: number }[] = [];
  loading = false;
  expandedStates: Set<string> = new Set();
  assignedBoardsByState: Map<string, number> = new Map();

  constructor(
    private boardStatService: BoardStatService,
    private locationService: LocationService,
    private loginService: LoginService,
    private cdr: ChangeDetectorRef,
    private boardService: BoardService
  ) {}
ngOnChanges(): void {
  if (this.boardStats?.length && this.startDate && this.endDate) {
    this.loading = true;

    const filteredStats = this.boardStats.filter(stat =>
      new Date(stat.timeStamp) >= new Date(this.startDate) &&
      new Date(stat.timeStamp) <= new Date(this.endDate) &&
      stat.daysSincePrevRead > 0 &&
      stat.cashInDiff > 0 &&
      stat.cashOutDiff >= 0 &&
      stat.board?.location?.salesRep?.firstName !== 'Warehouse'
    );

    const grouped = this.boardStatService.groupStatsByBoardDetails(
      filteredStats,
      this.boardStatService.stateKeyFunction
    );

    this.groupedStats = Array.from(grouped.entries()).map(([state, stats]) => ({
      state: state || 'Unknown',
      stats,
      uniqueBoardCount: this.getUniqueBoardCount(stats.rawStats)
    }));

    this.loading = false;
    this.cdr.detectChanges();
  }
}

  async ngOnInit(): Promise<void> {
    this.loading = true;

    try {
      const allLocations = await this.locationService.getAll();

      const enabledLocationIds = allLocations
        .filter(location => location.enabled === true)
        .map(location => location.locationTag);


      for (const location of allLocations) {
        if (
          location.enabled &&
          location.numBoards &&
          location.businessAddress?.state &&
          location.salesRep?.firstName !== 'Warehouse'
        ) {
          const state = location.businessAddress.state;
          const current = this.assignedBoardsByState.get(state) || 0;
          this.assignedBoardsByState.set(state, current + location.numBoards);
        }
      }

const filteredStats = this.boardStats.filter(item =>
  enabledLocationIds.includes(item.board?.location?.locationTag) &&
  item.daysSincePrevRead > 0 &&
  item.cashInDiff > 0 &&
  item.cashOutDiff >= 0 &&
  item.board?.location?.salesRep?.firstName !== 'Warehouse'
);

      const grouped = this.boardStatService.groupStatsByBoardDetails(
        filteredStats,
        this.boardStatService.stateKeyFunction
      );

      this.groupedStats = Array.from(grouped.entries()).map(([state, stats]) => {
        const uniqueBoardCount = this.getUniqueBoardCount(stats.rawStats);
        return {
          state: state || 'Unknown',
          stats,
          uniqueBoardCount
        };
      });

      const allFilteredBoardIds = filteredStats.map(s => s.board?.id).filter(Boolean);
      const uniqueFilteredBoardIds = new Set(allFilteredBoardIds);
      console.log('🎯 Total Filtered Unique Boards:', uniqueFilteredBoardIds.size);
      const totalGroupedUniqueBoards = this.getTotalScannedBoards();
      console.log('📊 Total Scanned (Group Sum):', totalGroupedUniqueBoards);

    } catch (err) {
      console.error('Error loading state stats:', err);
    }

    this.loading = false;
    this.cdr.detectChanges();
  }

  getGrandTotal(): GroupedStats {
    const total: GroupedStats = {
      avgDailyCashIn: 0, avgDailyNet: 0, avgDailyCoinIn: 0, avgDailyCoinOut: 0,
      avgDailyGamePlayed: 0, avgDailyGameWon: 0, avgDays: 0,
      sumDailyCashIn: 0, sumDailyNet: 0, sumDailyCoinIn: 0, sumDailyCoinOut: 0,
      sumDailyGamePlayed: 0, sumDailyGameWon: 0,
      totalCashin: 0, totalCashout: 0, totalCoinIn: 0, totalCoinOut: 0,
      totalGamesPlayed: 0, totalGamesWon: 0, totalNet: 0, totalDays: 0,
      winRatio: 0, payoutRatio: 0, rpt: 0, betMultiplier: 0, betPerPlay: 0,
      count: 0, rawStats: []
    };

    this.groupedStats.forEach(({ stats }) => {
      total.totalCashin += stats.totalCashin;
      total.totalCashout += stats.totalCashout;
      total.totalNet += stats.totalNet;
      total.totalCoinIn += stats.totalCoinIn;
      total.totalCoinOut += stats.totalCoinOut;
      total.totalGamesPlayed += stats.totalGamesPlayed;
      total.totalGamesWon += stats.totalGamesWon;
      total.totalDays += stats.totalDays;

      total.sumDailyCashIn += stats.sumDailyCashIn;
      total.sumDailyNet += stats.sumDailyNet;
      total.sumDailyCoinIn += stats.sumDailyCoinIn;
      total.sumDailyCoinOut += stats.sumDailyCoinOut;
      total.sumDailyGamePlayed += stats.sumDailyGamePlayed;
      total.sumDailyGameWon += stats.sumDailyGameWon;
      total.count += stats.count;
    });

    total.payoutRatio = total.totalCashin ? total.totalCashout / total.totalCashin : 0;
    total.rpt = total.totalCoinIn ? total.totalCoinOut / total.totalCoinIn : 0;
    total.winRatio = total.totalGamesPlayed ? total.totalGamesWon / total.totalGamesPlayed : 0;
    total.betMultiplier = total.sumDailyCashIn ? total.sumDailyCoinIn / total.sumDailyCashIn : 0;
    total.betPerPlay = total.sumDailyGamePlayed ? total.sumDailyCoinIn / total.sumDailyGamePlayed : 0;
    total.avgDays = total.count ? total.totalDays / total.count : 0;
    total.avgDailyCashIn = total.count ? total.sumDailyCashIn / total.count : 0;
    total.avgDailyNet = total.count ? total.sumDailyNet / total.count : 0;
    total.avgDailyCoinIn = total.count ? total.sumDailyCoinIn / total.count : 0;
    total.avgDailyCoinOut = total.count ? total.sumDailyCoinOut / total.count : 0;
    total.avgDailyGamePlayed = total.count ? total.sumDailyGamePlayed / total.count : 0;
    total.avgDailyGameWon = total.count ? total.sumDailyGameWon / total.count : 0;

    return total;
  }

  getCashInPerDay(stats: any): number {
    return stats.totalDays > 0 ? stats.totalCashin / stats.totalDays : 0;
  }

  getNetPerDay(stats: any): number {
    return stats.totalDays > 0 ? stats.totalNet / stats.totalDays : 0;
  }

  toggleState(state: string) {
    this.expandedStates.has(state) ? this.expandedStates.delete(state) : this.expandedStates.add(state);
  }

  isExpanded(state: string): boolean {
    return this.expandedStates.has(state);
  }

  getTotalForState(stats: any[], field: string): number {
    return stats.reduce((sum, stat) => sum + (stat[field] || 0), 0);
  }

  getAssignedBoardCount(state: string): number {
    return this.assignedBoardsByState.get(state) || 0;
  }

  getUniqueBoardCount(stats: any[]): number {
    const unique = new Set(stats.map(s => s.board?.id).filter(Boolean));
    return unique.size;
  }

  getTotalAssignedBoards(): number {
    let total = 0;
    for (const count of this.assignedBoardsByState.values()) {
      total += count;
    }
    return total;
  }

  getTotalScannedBoards(): number {
    return this.groupedStats.reduce((sum, group) => sum + group.uniqueBoardCount, 0);
  }



}
